import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import {
  ClipboardText, Plus, Trash, PencilSimple, X, Check, Calendar,
  User as UserIcon, Briefcase, Warning, Camera, Note as NoteIcon, Microphone
} from '@phosphor-icons/react';
import TaskCompletionModal from '../worker/TaskCompletionModal';
import VoiceDictateButton from '../VoiceDictateButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Contractor-facing "Daily Checklists" tab.
 * - List today's checklists (with a date picker)
 * - Create a new checklist (pick job → worker → date → tasks)
 * - Edit / delete existing checklists
 * - Watch progress live (poll every 15s)
 */
const ContractorChecklistsTab = () => {
  const [date, setDate] = useState(todayISO());
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // checklist object being edited

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/daily-checklists?date=${date}`, authHeader());
      setChecklists(res.data?.checklists || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  // Dashboard shortcut ("Voice Checklist" action button) opens the create form
  useEffect(() => {
    const open = () => setShowCreate(true);
    window.addEventListener('open-checklist-create', open);
    return () => window.removeEventListener('open-checklist-create', open);
  }, []);

  // Poll every 15s to catch worker check-offs
  useEffect(() => {
    const id = setInterval(fetchLists, 15000);
    return () => clearInterval(id);
  }, [fetchLists]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this checklist?')) return;
    try {
      await axios.delete(`${API}/daily-checklists/${id}`, authHeader());
      toast({ title: 'Deleted', description: 'Checklist removed.' });
      fetchLists();
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6" data-testid="contractor-checklists-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black uppercase tracking-tight flex items-center gap-2">
            <ClipboardText size={24} weight="fill" className="text-[#475569]" />
            Daily Checklists
            <span
              className="inline-flex items-center gap-1 bg-black text-[#475569] text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 border border-[#3B82F6]"
              data-testid="checklists-voice-badge"
            >
              <Microphone size={10} weight="fill" /> Voice
            </span>
          </h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Dictate or type daily tasks per worker — English or Spanish. They tap-to-check and you get notified in real-time.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="relative">
            <Calendar size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-11 pl-9 pr-3 border-2 border-black bg-white dark:bg-neutral-800 dark:text-white font-bold text-sm shadow-[2px_2px_0px_#000] rounded-none focus:outline-none"
              data-testid="checklists-date-picker"
            />
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 h-11 px-4 bg-[#3B82F6] hover:bg-[#1E40AF] text-[#0F172A] font-black uppercase text-sm tracking-wider border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-0.5 active:shadow-none rounded-none transition-all"
            data-testid="checklists-new-btn"
          >
            <Plus size={16} weight="bold" /> New Checklist
          </button>
        </div>
      </div>

      {/* List */}
      {loading && checklists.length === 0 ? (
        <div className="text-center py-12 text-neutral-500 text-sm">Loading...</div>
      ) : checklists.length === 0 ? (
        <div className="bg-white dark:bg-neutral-900 border-4 border-dashed border-neutral-300 dark:border-neutral-700 p-10 text-center" data-testid="checklists-empty">
          <ClipboardText size={40} weight="bold" className="mx-auto text-neutral-400 mb-3" />
          <p className="font-black uppercase text-neutral-600 dark:text-neutral-300 mb-1">No checklists for {date}</p>
          <p className="text-sm text-neutral-500 mb-4">Create one to assign daily tasks to a worker.</p>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 h-10 px-4 bg-[#3B82F6] text-[#0F172A] font-black uppercase text-xs tracking-wider border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] rounded-none transition-all"
          >
            <Plus size={14} weight="bold" /> New Checklist
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {checklists.map((c) => (
            <ChecklistCard
              key={c.id}
              checklist={c}
              onEdit={() => setEditing(c)}
              onDelete={() => handleDelete(c.id)}
              onRefresh={fetchLists}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <ChecklistFormModal
          initialDate={date}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); fetchLists(); }}
        />
      )}
      {editing && (
        <ChecklistFormModal
          initialDate={editing.date}
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchLists(); }}
        />
      )}
    </div>
  );
};


const ChecklistCard = ({ checklist, onEdit, onDelete, onRefresh }) => {
  const tasks = checklist.tasks || [];
  const done = tasks.filter(t => t.done).length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  const [previewTask, setPreviewTask] = useState(null);

  const toggle = async (taskId) => {
    try {
      await axios.post(`${API}/daily-checklists/${checklist.id}/tasks/${taskId}/toggle`, {}, authHeader());
      onRefresh();
    } catch {
      toast({ title: 'Update failed', variant: 'destructive' });
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-900 border-4 border-black shadow-[6px_6px_0px_#000] p-4" data-testid={`checklist-card-${checklist.id}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#475569] mb-1 truncate flex items-center gap-1">
            <Briefcase size={12} weight="fill" /> {checklist.job_title}
          </div>
          <h3 className="font-black text-lg leading-tight truncate">{checklist.title || 'Daily Tasks'}</h3>
          <div className="text-xs text-neutral-600 dark:text-neutral-400 mt-1 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><UserIcon size={12} weight="bold" /> {checklist.worker_name}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Calendar size={12} weight="bold" /> {checklist.date}</span>
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button
            onClick={onEdit}
            className="w-8 h-8 flex items-center justify-center bg-white dark:bg-neutral-800 border-2 border-black hover:bg-neutral-100 dark:hover:bg-neutral-700"
            data-testid={`checklist-edit-${checklist.id}`}
            title="Edit"
          >
            <PencilSimple size={14} weight="bold" />
          </button>
          <button
            onClick={onDelete}
            className="w-8 h-8 flex items-center justify-center bg-white dark:bg-neutral-800 border-2 border-black hover:bg-red-100 hover:border-red-600"
            data-testid={`checklist-delete-${checklist.id}`}
            title="Delete"
          >
            <Trash size={14} weight="bold" className="text-red-600" />
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs font-bold mb-1">
          <span>{done}/{tasks.length} done</span>
          <span className={pct === 100 ? 'text-emerald-600' : 'text-neutral-600'}>{pct}%</span>
        </div>
        <div className="h-2 bg-neutral-200 dark:bg-neutral-800 border border-black overflow-hidden">
          <div
            className={`h-full ${pct === 100 ? 'bg-emerald-500' : 'bg-[#3B82F6]'} transition-all`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Tasks */}
      {tasks.length === 0 ? (
        <p className="text-xs text-neutral-500 italic">No tasks yet. Edit to add.</p>
      ) : (
        <ul className="space-y-1.5" data-testid={`checklist-tasks-${checklist.id}`}>
          {tasks.map((t) => {
            const hasNote = !!t.note;
            const photoCount = (t.photo_ids || []).length;
            const hasProof = hasNote || photoCount > 0;
            return (
              <li key={t.id} className="flex items-start gap-2 group">
                <button
                  onClick={() => toggle(t.id)}
                  className={`flex-shrink-0 w-5 h-5 mt-0.5 border-2 border-black flex items-center justify-center transition-colors ${
                    t.done ? 'bg-emerald-500' : 'bg-white dark:bg-neutral-800 hover:bg-neutral-100'
                  }`}
                  data-testid={`checklist-task-toggle-${t.id}`}
                  title={t.done ? 'Mark undone' : 'Mark done'}
                >
                  {t.done && <Check size={14} weight="bold" className="text-white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <span className={`text-sm leading-snug ${t.done ? 'line-through text-neutral-400' : 'text-neutral-800 dark:text-neutral-200'}`}>
                    {t.text}
                  </span>
                  {hasProof && (
                    <button
                      onClick={() => setPreviewTask(t)}
                      className="ml-2 inline-flex items-center gap-1 align-middle hover:underline"
                      data-testid={`checklist-task-proof-${t.id}`}
                      title="View proof"
                    >
                      {photoCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-black text-white px-1.5 py-0.5">
                          <Camera size={10} weight="fill" /> {photoCount}
                        </span>
                      )}
                      {hasNote && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-neutral-200 dark:bg-neutral-700 dark:text-white px-1.5 py-0.5 border border-black">
                          <NoteIcon size={10} weight="fill" /> Note
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {previewTask && (
        <TaskCompletionModal
          task={previewTask}
          readOnly
          onSubmit={async () => {}}
          onClose={() => setPreviewTask(null)}
        />
      )}
    </div>
  );
};


/**
 * Modal for creating or editing a checklist.
 * Props:
 *   initialDate : YYYY-MM-DD (defaults today)
 *   editing     : (optional) full checklist object being edited
 */
const ChecklistFormModal = ({ initialDate, editing = null, onClose, onSaved }) => {
  const [date, setDate] = useState(editing?.date || initialDate);
  const [jobId, setJobId] = useState(editing?.job_id || '');
  const [workerId, setWorkerId] = useState(editing?.worker_id || '');
  const [title, setTitle] = useState(editing?.title || '');
  const [tasks, setTasks] = useState(
    (editing?.tasks || []).map(t => ({ text: t.text })).concat(editing ? [] : [{ text: '' }])
  );
  const [jobs, setJobs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load contractor's jobs on mount (only for create mode; edit mode uses existing job_id)
  useEffect(() => {
    if (editing) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/jobs/my-jobs`, authHeader());
        setJobs(res.data?.jobs || res.data || []);
      } catch {
        // fallback
      }
    })();
  }, [editing]);

  // When jobId changes, load workers assigned to that job
  useEffect(() => {
    if (!jobId) { setWorkers([]); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/daily-checklists/job/${jobId}/workers`, authHeader());
        setWorkers(res.data?.workers || []);
      } catch {
        setWorkers([]);
      }
    })();
  }, [jobId]);

  const addTask = () => setTasks((prev) => [...prev, { text: '' }]);

  const addVoiceTasks = (data) => {
    const incoming = (data.tasks || []).map((t) => ({ text: t }));
    if (!incoming.length) {
      toast({ title: 'No tasks detected', description: 'Try again — e.g. "frame the wall, then clean up debris".', variant: 'destructive' });
      return;
    }
    setTasks((prev) => [...prev.filter((t) => (t.text || '').trim()), ...incoming]);
    toast({ title: `${incoming.length} task${incoming.length !== 1 ? 's' : ''} added from voice` });
  };
  const removeTask = (i) => setTasks((prev) => prev.filter((_, idx) => idx !== i));
  const updateTask = (i, v) => setTasks((prev) => prev.map((t, idx) => (idx === i ? { text: v } : t)));

  const save = async () => {
    setError('');
    const cleaned = tasks.map(t => ({ text: (t.text || '').trim() })).filter(t => t.text);
    if (!editing) {
      if (!jobId) return setError('Pick a job');
      if (!workerId) return setError('Pick a worker');
    }
    if (!date) return setError('Pick a date');
    if (cleaned.length === 0) return setError('Add at least one task');

    setSaving(true);
    try {
      if (editing) {
        await axios.put(
          `${API}/daily-checklists/${editing.id}`,
          { title: title || null, tasks: cleaned },
          authHeader(),
        );
        toast({ title: 'Updated', description: 'Checklist saved.' });
      } else {
        await axios.post(
          `${API}/daily-checklists`,
          { job_id: jobId, worker_id: workerId, date, title: title || null, tasks: cleaned },
          authHeader(),
        );
        toast({ title: 'Created', description: 'Checklist assigned.' });
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-neutral-900 border-4 border-black shadow-[8px_8px_0px_#000] w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="checklist-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-4 border-black bg-[#3B82F6] text-[#0F172A]">
          <div className="flex items-center gap-2">
            <ClipboardText size={20} weight="fill" />
            <h3 className="font-black text-lg uppercase">{editing ? 'Edit Checklist' : 'New Checklist'}</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-black text-white border-2 border-white hover:bg-neutral-900"
            data-testid="checklist-modal-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {!editing && (
            <>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-neutral-600 dark:text-neutral-400">Job</label>
                <select
                  value={jobId}
                  onChange={(e) => setJobId(e.target.value)}
                  className="w-full h-10 mt-1 px-3 border-2 border-black bg-white dark:bg-neutral-800 dark:text-white font-bold text-sm shadow-[2px_2px_0px_#000] rounded-none focus:outline-none"
                  data-testid="checklist-job-select"
                >
                  <option value="">— pick a job —</option>
                  {jobs.map(j => (
                    <option key={j._id || j.id} value={j._id || j.id}>{j.title || j.job_title || 'Job'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-neutral-600 dark:text-neutral-400">Worker</label>
                <select
                  value={workerId}
                  onChange={(e) => setWorkerId(e.target.value)}
                  disabled={!jobId}
                  className="w-full h-10 mt-1 px-3 border-2 border-black bg-white dark:bg-neutral-800 dark:text-white font-bold text-sm shadow-[2px_2px_0px_#000] rounded-none focus:outline-none disabled:opacity-50"
                  data-testid="checklist-worker-select"
                >
                  <option value="">{jobId ? (workers.length ? '— pick a worker —' : 'No accepted workers on this job') : 'Pick a job first'}</option>
                  {workers.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-neutral-600 dark:text-neutral-400">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full h-10 mt-1 px-3 border-2 border-black bg-white dark:bg-neutral-800 dark:text-white font-bold text-sm shadow-[2px_2px_0px_#000] rounded-none focus:outline-none"
                  data-testid="checklist-date-input"
                />
              </div>
            </>
          )}
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-neutral-600 dark:text-neutral-400">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Framing crew — morning shift"
              className="w-full h-10 mt-1 px-3 border-2 border-black bg-white dark:bg-neutral-800 dark:text-white font-bold text-sm shadow-[2px_2px_0px_#000] rounded-none focus:outline-none"
              data-testid="checklist-title-input"
            />
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-neutral-600 dark:text-neutral-400 flex items-center justify-between">
              <span>Tasks</span>
              <span className="text-neutral-500 normal-case tracking-normal">{tasks.filter(t => (t.text || '').trim()).length}</span>
            </label>
            <div className="space-y-2 mt-1" data-testid="checklist-tasks-inputs">
              {tasks.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center bg-black text-white text-[10px] font-black flex-shrink-0">{i + 1}</span>
                  <input
                    type="text"
                    value={t.text}
                    onChange={(e) => updateTask(i, e.target.value)}
                    placeholder="Task…"
                    className="flex-1 h-9 px-3 border-2 border-black bg-white dark:bg-neutral-800 dark:text-white text-sm rounded-none focus:outline-none"
                    data-testid={`checklist-task-input-${i}`}
                  />
                  <button
                    onClick={() => removeTask(i)}
                    className="w-8 h-8 flex items-center justify-center bg-white dark:bg-neutral-800 border-2 border-black hover:bg-red-100"
                    data-testid={`checklist-task-remove-${i}`}
                    title="Remove"
                  >
                    <X size={14} weight="bold" className="text-red-600" />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <button
                onClick={addTask}
                className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-[#475569] hover:underline"
                data-testid="checklist-add-task-btn"
              >
                <Plus size={12} weight="bold" /> Add Task
              </button>
              <VoiceDictateButton mode="tasks" onResult={addVoiceTasks} />
            </div>
            <p className="mt-1.5 text-[10px] text-neutral-500">
              Speak freely in English or Spanish — AI splits it into separate tasks.
            </p>
          </div>

          {error && (
            <div className="p-2 bg-red-100 border-2 border-red-600 text-red-800 text-xs font-bold flex items-center gap-2">
              <Warning size={14} weight="fill" /> {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t-4 border-black bg-neutral-50 dark:bg-neutral-800 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 px-4 bg-white dark:bg-neutral-900 dark:text-white font-black uppercase text-xs tracking-widest border-2 border-black hover:bg-neutral-100 rounded-none"
            data-testid="checklist-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="h-10 px-4 bg-[#3B82F6] hover:bg-[#1E40AF] text-[#0F172A] font-black uppercase text-xs tracking-widest border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-0.5 active:shadow-none rounded-none transition-all disabled:opacity-50"
            data-testid="checklist-save-btn"
          >
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Checklist'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractorChecklistsTab;
